import { UID } from '@fullstory/util/uid';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { identifyComponent } from '../../util';
import { CollapsibleSectionContext } from './Context';

export interface CollapsibleSectionProps {
  /**
   * By default the section is collapsed. If this is set to `true` then the section will be expanded by default.
   */
  initiallyCollapsed?: boolean;

  /**
   * An `id` that will be used on different elements to create the correct accessibility associatons.
   * If not provided a UID will be used instead.
   */
  id?: string;

  /**
   * Optional callback that fires when the collapsed value has changed
   */
  onCollapsedChange?: (collapsed: boolean) => void;

  /**
   * `testId` gets passed onto the `data-testid` attribute.
   */
  testId?: string;
}

/**
 * An CollapsibleSection can be used to create a collapsible section of content that is controlled by a header.
 */
export const CollapsibleSection: React.FunctionComponent<
  PropsWithChildren<CollapsibleSectionProps>
> = ({
  children,
  initiallyCollapsed = true,
  id: explicitId,
  onCollapsedChange,
  testId = 'collapsible-section',
}) => {
  const [id] = useState<string>(explicitId ?? UID());
  const [collapsed, setCollapsed] = useState<boolean>(initiallyCollapsed);
  const [hasLabel, setHasLabel] = useState<boolean>(false);

  const setCollapsedChange = useCallback(
    (val: boolean) => {
      setCollapsed(val);
      onCollapsedChange?.(val);
    },
    [onCollapsedChange],
  );

  return (
    <CollapsibleSectionContext.Provider
      value={{ id, collapsed, setCollapsed: setCollapsedChange, setHasLabel, hasLabel, testId }}
    >
      <div data-component="CollapsibleSection" {...identifyComponent('collapsible-section')}>
        {children}
      </div>
    </CollapsibleSectionContext.Provider>
  );
};
